import { editableOffers, sellablePlans } from '@entities/product'
import type { Product } from '@entities/product'
import type { RecurrencePlan } from '@entities/product/api/product.contract'

export type OfferProductOption = {
  product: Product
  value: string
  offerId: string
  price: Product['price']
  label: string
  priceLabel: string
  planId: string | null
  plan: RecurrencePlan | null
}

const PERIOD_SUFFIX: Record<RecurrencePlan['billingPeriodUnit'], [string, string]> = {
  MINUTE: ['/minuto', 'minutos'],
  DAY: ['/dia', 'dias'],
  WEEK: ['/semana', 'semanas'],
  MONTH: ['/mês', 'meses'],
  YEAR: ['/ano', 'anos'],
}

export function planPeriodSuffix(
  plan: Pick<RecurrencePlan, 'billingPeriodUnit' | 'billingPeriodCount'>,
): string {
  const suffix = PERIOD_SUFFIX[plan.billingPeriodUnit] ?? PERIOD_SUFFIX.MONTH
  return plan.billingPeriodCount > 1 ? ` a cada ${plan.billingPeriodCount} ${suffix[1]}` : suffix[0]
}

export function planTrialBadge(plan: RecurrencePlan): string {
  if (!plan.trialEnabled || plan.trialDays <= 0) return ''
  return plan.trialType === 'WITH_PAYMENT'
    ? ` · ${plan.trialDays}d grátis`
    : ` · teste de ${plan.trialDays}d`
}

function formatOptionMoney(price: Product['price']): string {
  const amount = Number(price.amount)
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: price.currency }).format(
    Number.isFinite(amount) ? amount : 0,
  )
}

export function planSelectionValue(planId: string): string {
  return `plan:${planId}`
}

export type OfferSelectionRef = {
  offerId: string
  planId: string | null
}

export function funnelSelectionsOf(product: Product): OfferSelectionRef[] {
  return [
    ...(product.orderBumps ?? []).map((bump) => ({
      offerId: bump.targetOfferId,
      planId: bump.planId,
    })),
    ...(product.funnelNodes ?? []).map((node) => ({ offerId: node.offerId, planId: node.planId })),
  ]
}

export function getOfferProductOptions(
  product: Product,
  products: Product[] | undefined,
  pinnedSelections: OfferSelectionRef[] = [],
): OfferProductOption[] {
  const orderedProducts = [product, ...(products ?? [])]
  const seen = new Set<string>()
  const pinnedPlanIds = new Set(
    pinnedSelections.map((selection) => selection.planId).filter((planId) => planId !== null),
  )
  const pinnedOfferIds = new Set(
    pinnedSelections
      .filter((selection) => selection.planId === null)
      .map((selection) => selection.offerId),
  )

  return orderedProducts.reduce<OfferProductOption[]>((options, item) => {
    if (seen.has(item.id)) return options
    seen.add(item.id)

    const activePlans = (item.recurrencePlans ?? []).filter((plan) => plan.active)
    if (item.offers.length === 0 && activePlans.length === 0) {
      options.push({
        product: item,
        value: `product:${item.id}`,
        offerId: `product:${item.id}`,
        price: item.price,
        label: item.name,
        priceLabel: formatOptionMoney(item.price),
        planId: null,
        plan: null,
      })
      return options
    }

    const limitedOffers = editableOffers(item)
    const legacyOffers = item.offers.filter(
      (offer) =>
        pinnedOfferIds.has(offer.id) && !limitedOffers.some((visible) => visible.id === offer.id),
    )
    const listedOffers = [...limitedOffers, ...legacyOffers]
    const hasMultipleOffers = listedOffers.length > 1
    for (const offer of listedOffers) {
      options.push({
        product: item,
        value: offer.id,
        offerId: offer.id,
        price: offer.price,
        label: hasMultipleOffers ? `${item.name} · ${offer.name}` : item.name,
        priceLabel: formatOptionMoney(offer.price),
        planId: null,
        plan: null,
      })
    }

    const anchorOfferId = listedOffers[0]?.id ?? `product:${item.id}`
    const limitedPlans = sellablePlans(item)
    const legacyPlans = (item.recurrencePlans ?? []).filter(
      (plan) =>
        pinnedPlanIds.has(plan.id) && !limitedPlans.some((visible) => visible.id === plan.id),
    )
    const listedPlans = [...limitedPlans, ...legacyPlans]
    const shouldQualifyPlanLabel = listedOffers.length > 0 || listedPlans.length > 1
    for (const plan of listedPlans) {
      options.push({
        product: item,
        value: planSelectionValue(plan.id),
        offerId: anchorOfferId,
        price: plan.value,
        label: shouldQualifyPlanLabel ? `${item.name} · ${plan.name}` : item.name,
        priceLabel: `${formatOptionMoney(plan.value)}${planPeriodSuffix(plan)}${planTrialBadge(plan)}`,
        planId: plan.id,
        plan,
      })
    }

    return options
  }, [])
}

export function findOfferProductOption(options: OfferProductOption[], value: string) {
  return options.find((option) => option.value === value) ?? options[0]
}

export function findOptionForSelection(
  options: OfferProductOption[],
  offerId: string,
  planId: string | null,
) {
  if (planId !== null) {
    return (
      options.find((option) => option.planId === planId) ??
      options.find((option) => option.offerId === offerId) ??
      options[0]
    )
  }
  return (
    options.find((option) => option.planId === null && option.offerId === offerId) ??
    options.find((option) => option.offerId === offerId) ??
    options[0]
  )
}

export function synchronizeFunnelPlanPrices(
  product: Product,
  products: Product[] | undefined,
): Product {
  const options = getOfferProductOptions(product, products, funnelSelectionsOf(product))
  let changed = false
  const currentPlanPrice = (
    offerId: string,
    planId: string | null,
    storedPrice: Product['price'],
  ) => {
    if (planId === null) return storedPrice
    const option = findOptionForSelection(options, offerId, planId)
    if (option?.planId !== planId || option.plan === null) return storedPrice
    if (
      option.plan.value.amount === storedPrice.amount &&
      option.plan.value.currency === storedPrice.currency
    ) {
      return storedPrice
    }
    changed = true
    return option.plan.value
  }
  const orderBumps = product.orderBumps.map((bump) => ({
    ...bump,
    price: currentPlanPrice(bump.targetOfferId, bump.planId, bump.price),
  }))
  const funnelNodes = product.funnelNodes.map((node) => ({
    ...node,
    price: currentPlanPrice(node.offerId, node.planId, node.price),
  }))

  return changed ? { ...product, orderBumps, funnelNodes } : product
}
